import { useEffect, useLayoutEffect, useRef } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { motion } from "motion/react"
import { Movie, MovieSession } from "../../types/entities/movie"
import { groupMovieSessions, shouldGroupSessionsByDate } from "../../lib/sessionGroups"
import { unpaidOrderStore } from "../../store/unpaidOrderStore"
import { isUnpaid } from "../tickets/ticketUtils"

const MONO = "'JetBrains Mono', monospace"

function Session3DBadge({ selected }: { selected?: boolean }) {
    const accent = selected ? "#e4206b" : "#349be6"

    return (
        <div className="relative flex size-[26px] items-center justify-center">
            <div
                className="pointer-events-none absolute size-7 rounded-full blur-[5px]"
                style={{ background: "#349be6", opacity: 0.45, top: -2, left: -3 }}
            />
            <div
                className="pointer-events-none absolute size-7 rounded-full blur-[5px]"
                style={{ background: "#e4206b", opacity: 0.4, top: -2, right: -3 }}
            />
            <div
                className="relative flex size-[26px] items-center justify-center rounded-full bg-white"
                style={{ boxShadow: "0 2px 10px rgba(0,0,0,0.22)" }}>
                <span
                    className="text-[8px] leading-none"
                    style={{ fontFamily: MONO, fontWeight: 800, color: accent, letterSpacing: "-0.03em" }}>
                    3D
                </span>
            </div>
        </div>
    )
}

type SessionStringProps = {
    sessions: Movie["sessions"]
    movieData: { privileges: Movie["privileges"]; poster: string; poster_mobile: string }
    selectedSession?: number
    setSelectedSession?: (id: number) => void
    center?: boolean
    scrollable?: boolean
    /** Показывать даты (подборки и сеансы на разные дни) */
    showDates?: boolean
}

export default function SessionString({
    sessions,
    movieData,
    selectedSession,
    setSelectedSession,
    center,
    scrollable,
    showDates = false,
}: SessionStringProps) {
    const location = useLocation()
    const unpaidTicket = unpaidOrderStore(state => state.unpaidTicket)
    const blockedByUnpaid = !!unpaidTicket && isUnpaid(unpaidTicket)
    const groupByDate = shouldGroupSessionsByDate(sessions, showDates)
    const sessionGroups = groupByDate ? groupMovieSessions(sessions) : [{ date: "", label: "", sessions }]

    const scrollRef = useRef<HTMLDivElement>(null)
    const selectedItemRef = useRef<HTMLDivElement>(null)
    const didInitialScrollRef = useRef(false)

    const revealSelected = (smooth: boolean) => {
        const container = scrollRef.current
        const el = selectedItemRef.current
        if (!container || !el) return

        const pad = 16
        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        let delta = 0
        if (elRect.left < containerRect.left + pad) {
            delta = elRect.left - containerRect.left - pad
        } else if (elRect.right > containerRect.right - pad) {
            delta = elRect.right - containerRect.right + pad
        }
        if (Math.abs(delta) < 1) return

        container.scrollTo({
            left: container.scrollLeft + delta,
            behavior: smooth ? "smooth" : "auto",
        })
    }

    useLayoutEffect(() => {
        if (!scrollable || !selectedSession) return

        const container = scrollRef.current
        if (!container) return

        const smooth = didInitialScrollRef.current
        revealSelected(smooth)
        didInitialScrollRef.current = true

        const ro = new ResizeObserver(() => revealSelected(false))
        ro.observe(container)
        return () => ro.disconnect()
    }, [scrollable, selectedSession, sessions])

    useEffect(() => {
        if (!scrollable) return
        const container = scrollRef.current
        if (!container) return

        const onWheel = (e: WheelEvent) => {
            if (container.scrollWidth <= container.clientWidth) return
            if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return
            e.preventDefault()
            container.scrollLeft += e.deltaY
        }

        container.addEventListener("wheel", onWheel, { passive: false })
        return () => container.removeEventListener("wheel", onWheel)
    }, [scrollable, sessions])

    const rowClassName = `flex gap-3 ${
        scrollable ? "w-max min-w-full flex-nowrap px-4" : "w-full flex-wrap"
    } ${center ? "justify-center" : ""}`

    const renderSession = (item: MovieSession) => {
        const isSelected = selectedSession === item.id
        const is3d = item.format.toLowerCase() === "3d"
        const bg = isSelected ? "#e4206b" : "#349be6"
        const shadow = isSelected ? "0 0 18px rgba(228,32,107,0.5)" : "0 0 14px rgba(52,155,230,0.4)"
        const glow3d = isSelected
            ? "0 0 18px rgba(228,32,107,0.55), 0 0 32px rgba(52,155,230,0.22)"
            : "0 0 14px rgba(52,155,230,0.42), 0 0 24px rgba(228,32,107,0.28)"
        const ringGradient = isSelected
            ? "linear-gradient(135deg, #e4206b 0%, #ff6b9d 50%, #349be6 100%)"
            : "linear-gradient(135deg, #349be6 0%, #7dd3fc 45%, #e4206b 100%)"

        const inner = (
            <motion.div
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.93 }}
                className="flex cursor-pointer flex-col items-center gap-1">
                {is3d ? (
                    <div className="flex flex-col items-center">
                        <div className="relative z-10">
                            <Session3DBadge selected={isSelected} />
                        </div>
                        <div
                            className="relative -mt-1.5"
                            style={{ boxShadow: glow3d, borderRadius: 11 }}>
                            <div
                                className="absolute -inset-px rounded-[11px]"
                                style={{ background: ringGradient }}
                            />
                            <div className="relative flex h-8 w-14 items-center justify-center rounded-[10px] bg-[#16284a]">
                                <span className="text-[13px] text-white" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    {item.time}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="flex h-8 w-14 items-center justify-center rounded-[10px] transition-all duration-150 hover:brightness-110 active:brightness-90"
                        style={{ background: bg, boxShadow: shadow }}>
                        <span className="text-[13px] text-white" style={{ fontFamily: MONO, fontWeight: 700 }}>
                            {item.time}
                        </span>
                    </div>
                )}
                <span
                    className="text-[12px]"
                    style={{
                        fontFamily: MONO,
                        color: is3d ? (isSelected ? "rgba(228,32,107,0.75)" : "rgba(125,211,252,0.72)") : "rgba(255,255,255,0.5)",
                    }}>
                    {item.price_name}
                </span>
            </motion.div>
        )

        if (!selectedSession) {
            if (blockedByUnpaid) {
                return (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => unpaidOrderStore.getState().openNotice()}
                        className="flex cursor-pointer flex-col items-center justify-end gap-0.5 border-0 bg-transparent p-0">
                        {inner}
                    </button>
                )
            }

            return (
                <NavLink
                    key={item.id}
                    to="/buy"
                    state={{ sessions, movieData, backgroundLocation: location, sessionId: item.id }}
                    className="flex cursor-pointer flex-col items-center justify-end gap-0.5">
                    {inner}
                </NavLink>
            )
        }

        return (
            <div
                key={item.id}
                ref={isSelected ? selectedItemRef : undefined}
                onClick={() => setSelectedSession?.(item.id)}
                className="flex cursor-pointer flex-col items-center justify-end gap-0.5">
                {inner}
            </div>
        )
    }

    const content = groupByDate ? (
        <div className="flex flex-col gap-4">
            {sessionGroups.map(group => (
                <div key={group.date || group.label} className="flex flex-col gap-2">
                    <p className="text-[13px] text-white/50" style={{ fontFamily: MONO, fontWeight: 700 }}>
                        {group.label}
                    </p>
                    <div className={rowClassName}>{group.sessions.map(renderSession)}</div>
                </div>
            ))}
        </div>
    ) : (
        <div className={rowClassName}>{sessions.map(renderSession)}</div>
    )

    if (scrollable) {
        return (
            <div
                ref={scrollRef}
                className="w-full min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain py-5 -my-3 scrollbar-hide"
                style={{ touchAction: "pan-x", WebkitOverflowScrolling: "touch" }}>
                {content}
            </div>
        )
    }

    return content
}
